// =====================================================================
// Modal — the ONE canonical centered-dialog pattern for the app.
//
// Fixes the "backdrop doesn't cover the top edge" bug reported across
// Schema > Tables, Schema > Migrations, and elsewhere: every prior modal
// rendered `fixed inset-0` INLINE as a sibling among a page's other
// sections — usually inside a `space-y-*` stack. Tailwind's `space-y-*`
// applies `margin-top` to every non-first child via
// `> :not([hidden]) ~ :not([hidden])`, and that margin lands on the
// modal's own `fixed inset-0` box too (fixed positioning resolves `inset`
// against the viewport, but the box model still carries whatever margin
// the cascade gives it) — shearing the exact space-y gap (e.g. 24px) off
// the backdrop's top edge and leaving the header visibly undimmed above
// it. Confirmed empirically: a `fixed inset-0` panel nested inside
// `<div className="space-y-6">` computed `margin-top: 24px` and its
// rendered box started at y=24, not y=0.
//
// The fix: `createPortal` to `document.body`. That removes the modal from
// its parent's child list entirely, so no sibling-spacing utility (now or
// in the future) can ever apply margin to it again — this isn't just a
// one-off CSS tweak, it's structurally immune to the whole class of bug.
//
// Every modal in the app should render through this component (or
// <Drawer> for the off-canvas/side-panel variant) rather than hand-rolling
// `fixed inset-0` + focus/escape/scroll-lock wiring again.
// =====================================================================

import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, useReducedMotion } from 'framer-motion'
import useModalFocusTrap from '../../hooks/useModalFocusTrap'

const Modal = ({
  open,
  onClose,
  children,
  role = 'dialog',
  ariaLabelledBy,
  ariaDescribedBy,
  maxWidth = 'max-w-lg',
  maxHeight = 'max-h-[90vh]',
  panelClassName = '',
  backdropClassName = 'bg-gray-900/50 backdrop-blur-sm',
  closeOnBackdropClick = true,
  scrollable = true,
  initialFocusRef,
}) => {
  const panelRef = useRef(null)
  const reduceMotion = useReducedMotion()

  useModalFocusTrap(panelRef, onClose, open)

  // Body scroll-lock while open — a full-viewport modal with a scrollable
  // page behind it otherwise lets the page (and any position:sticky
  // headers in it) scroll underneath the backdrop.
  useEffect(() => {
    if (!open) return undefined
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = overflow }
  }, [open])

  // Honor an explicit initial-focus target (e.g. ConfirmModal defaulting
  // to Cancel, never the destructive Confirm) once the trap above has run.
  useEffect(() => {
    if (!open || !initialFocusRef?.current) return
    const raf = requestAnimationFrame(() => initialFocusRef.current?.focus())
    return () => cancelAnimationFrame(raf)
  }, [open, initialFocusRef])

  if (!open || typeof document === 'undefined') return null

  return createPortal(
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center p-4 ${backdropClassName}`}
      onMouseDown={(e) => {
        if (closeOnBackdropClick && e.target === e.currentTarget) onClose?.()
      }}
    >
      <motion.div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1}
        initial={reduceMotion ? false : { opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.2 }}
        onMouseDown={(e) => e.stopPropagation()}
        className={`relative w-full ${maxWidth} ${scrollable ? `${maxHeight} overflow-y-auto` : ''} rounded-2xl bg-white shadow-elev-3 border border-gray-200/80 focus:outline-none ${panelClassName}`}
      >
        {children}
      </motion.div>
    </div>,
    document.body
  )
}

export default Modal
